import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type GuaranteeEnrollment } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, StatTile, type Column } from '@/components/ui';
import { IconShield, IconUsers, IconWallet } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { dateTime } from '@/lib/adminFormat';

const PAGE = 25;

/** Money formatter for the AUD figures on this page ($X.XX). The codebase has no
 *  shared currency helper (it formats km inline the same way), so this keeps the
 *  enrollment view self-contained and consistent. */
function money(n: number | null | undefined): string {
  return `$${(n ?? 0).toFixed(2)}`;
}

/** Guarantee Enrollments — the per-driver enrollment + progress log for the
 *  weekly *earnings guarantees*. An earnings guarantee promises a driver at least
 *  a guaranteed amount for the period if they complete the target trips; the
 *  platform tops up any shortfall. Drivers are auto-enrolled when they open the
 *  Opportunities feed while a guarantee is active, and incentives-svc advances
 *  `trips_completed` / `earnings_so_far` off the trip stream. incentives-svc OWNS
 *  the `driver_guarantee_enrollments` table; admin-svc only reads it.
 *
 *  READ-ONLY here — by design. The legacy Filament resource
 *  (DriverGuaranteeEnrollmentResource) was itself read-only (canCreate() = false):
 *  enrollments are auto-created and progress is machine-advanced, and
 *  incentives-svc exposes no admin create/edit/delete endpoint, so there is
 *  nothing for admin-svc to proxy a write to (and admin-svc never writes a table
 *  another service owns). This is a clean list + detail VIEW (no dead buttons).
 *  The section is DEFENSIVE (shows a "not yet available" state when the table
 *  isn't in this Postgres). Covers the legacy resource's Driver / Guarantee /
 *  Trips-done / Earnings columns + its per-driver filter (here surfaced as a
 *  per-guarantee filter, the more useful scoping on a paged list). */
export function GuaranteeEnrollments() {
  const [q, setQ] = useState('');
  const [guaranteeId, setGuaranteeId] = useState<number | ''>('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<GuaranteeEnrollment | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'guarantee-enrollments', q, guaranteeId, offset],
    queryFn: ({ signal }) =>
      adminApi.guaranteeEnrollments(
        { q, guarantee_id: guaranteeId === '' ? undefined : guaranteeId, limit: PAGE, offset },
        signal,
      ),
    placeholderData: (prev) => prev,
  });

  // Stats roll-up (count / drivers / guarantees / earnings) — its own light query
  // so the tiles render even while the table page is refetching.
  const stats = useQuery({
    queryKey: ['admin', 'guarantee-enrollments', 'stats'],
    queryFn: ({ signal }) => adminApi.guaranteeEnrollmentStats(signal),
  });

  const unavailable = data && data.available === false;
  const guaranteeFilters = data?.guarantee_filters ?? [];

  const columns: Array<Column<GuaranteeEnrollment>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-muted">#{r.id}</span> },
    {
      key: 'driver',
      header: 'Driver',
      cell: (r) => <span className="font-medium text-slate-100">{r.driver_label}</span>,
    },
    {
      key: 'guarantee',
      header: 'Guarantee',
      cell: (r) => <span className="text-slate-300">{r.guarantee_label}</span>,
    },
    {
      key: 'trips',
      header: 'Trips done',
      align: 'right',
      cell: (r) => <span className="metric text-sm text-brand-400">{r.trips_completed}</span>,
    },
    {
      key: 'earnings',
      header: 'Earnings so far',
      align: 'right',
      cell: (r) => <span className="metric text-sm text-ok">{money(r.earnings_so_far)}</span>,
    },
    {
      key: 'updated',
      header: 'Updated',
      cell: (r) => <span className="text-xs text-muted">{dateTime(r.updated_at)}</span>,
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Guarantee Enrollments"
        subtitle="Per-driver enrollment + progress for the weekly earnings guarantees."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search by driver or guarantee…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Guarantee enrollments not yet in Postgres"
            description={
              data?.note ??
              'The driver_guarantee_enrollments table is owned by incentives-svc and is not in this Postgres yet.'
            }
            icon={<IconShield className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {/* Stat tiles — glance figures over the enrollment set. */}
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile
              label="Total enrollments"
              value={stats.data?.stats?.total ?? '—'}
              icon={<IconShield className="h-4 w-4" />}
            />
            <StatTile
              label="Drivers"
              value={stats.data?.stats?.drivers ?? '—'}
              tone="info"
              icon={<IconUsers className="h-4 w-4" />}
              hint="enrolled"
            />
            <StatTile label="Guarantees" value={stats.data?.stats?.guarantees ?? '—'} hint="with enrollments" />
            <StatTile
              label="Earnings tracked"
              value={stats.data?.stats ? money(stats.data.stats.earnings_so_far) : '—'}
              tone="ok"
              icon={<IconWallet className="h-4 w-4" />}
              hint="across all enrollments"
            />
          </div>

          {/* Read-only notice — enrollments are auto-created + machine-advanced; no
              authoring endpoint exists upstream. Keeps the page honest: no dead buttons. */}
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-ink-800 bg-ink-850 px-3 py-2 text-xs text-text-secondary">
            <IconShield className="mt-0.5 h-4 w-4 shrink-0 text-brand-400" />
            <span>
              Read-only view. Drivers are auto-enrolled when they open the Opportunities screen while a guarantee is
              active, and progress is advanced automatically from the trip stream by incentives-svc. Top-ups settle
              through the wallet ledger.
            </span>
          </div>

          {/* Guarantee filter — mirrors the legacy Filament SelectFilter. Only
              guarantees that actually have enrollments appear, so it never lists an
              empty option. Hidden until the list reports at least one. */}
          {guaranteeFilters.length > 0 ? (
            <div className="mb-3 flex items-center gap-2">
              <label className="text-xs text-text-secondary">Guarantee</label>
              <select
                value={guaranteeId}
                onChange={(e) => {
                  setGuaranteeId(e.target.value === '' ? '' : Number(e.target.value));
                  setOffset(0);
                }}
                className="input h-8 w-auto py-0 text-xs"
              >
                <option value="">All guarantees</option>
                {guaranteeFilters.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.period || `Guarantee #${f.id}`}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading enrollments…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load enrollments" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<GuaranteeEnrollment>
                  columns={columns}
                  rows={data?.items ?? []}
                  rowKey={(r) => String(r.id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No enrollments"
                      description={
                        q || guaranteeId !== ''
                          ? 'No guarantee enrollments match this filter.'
                          : 'No drivers have been enrolled in an earnings guarantee yet.'
                      }
                      icon={<IconShield className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected !== null ? (
        <GuaranteeEnrollmentDrawer enrollment={selected} onClose={() => setSelected(null)} />
      ) : null}
    </AdminPage>
  );
}

// ─── enrollment detail drawer (read-only) ────────────────────────

/** GuaranteeEnrollmentDrawer — the read-only detail panel for one enrollment.
 *  Seeded directly from the row (the list already carries the full row), so it
 *  opens instantly. Purely informational, matching the read-only surface. */
function GuaranteeEnrollmentDrawer({
  enrollment,
  onClose,
}: {
  enrollment: GuaranteeEnrollment;
  onClose: () => void;
}) {
  return (
    <Drawer title={`Enrollment #${enrollment.id}`} subtitle={enrollment.guarantee_label} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-baseline gap-4">
          <div className="flex items-baseline gap-2">
            <span className="metric text-2xl font-semibold text-brand-400">{enrollment.trips_completed}</span>
            <span className="text-sm text-text-secondary">trips</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="metric text-2xl font-semibold text-ok">{money(enrollment.earnings_so_far)}</span>
            <span className="text-sm text-text-secondary">so far</span>
          </div>
        </div>

        <Card>
          <div>
            <Field label="ID" value={`#${enrollment.id}`} />
            <Field label="Driver" value={enrollment.driver_label} />
            <Field label="Guarantee" value={enrollment.guarantee_label} />
            <Field label="Trips done" value={enrollment.trips_completed} />
            <Field label="Earnings so far" value={money(enrollment.earnings_so_far)} />
            <Field label="Enrolled" value={dateTime(enrollment.created_at)} />
            <Field label="Last updated" value={dateTime(enrollment.updated_at)} />
          </div>
        </Card>

        <p className="text-[11px] text-text-muted">
          Read-only. This enrollment was auto-created when the driver opened the Opportunities feed; progress is advanced
          automatically by incentives-svc from the trip stream, and any guaranteed top-up settles through the wallet
          ledger.
        </p>
      </div>
    </Drawer>
  );
}
